import React, { useCallback, useMemo } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'
import * as R from 'ramda'

import FilterGroup from 'shared/components/FilterGroup'
import FilterGroupV2 from 'shared/components/FilterGroupV2'
import { Dropdown } from 'semantic-ui-react'
import { AutoFocusSemanticUiComponent } from 'components/AutoFocusSemanticUiComponent'
import useGetComparisons from 'hooks/useGetComparisons'
import { decodeFilterFromUrlParam, encodeFilterToUrlParam } from 'shared/components/FilterGroupV2/utils'
import { languageOption } from './constants'
import useFilterOptions from 'hooks/useFilterOptions'

/**
 * @template T
 * @typedef {import('shared/components/FilterGroup').Option<T>} Option
 */
/** @typedef {{attribute?: string}} Extra */
/**
 * @template T
 * @typedef {import('shared/components/FilterGroup').OnChangeValue<T>} OnChangeValue
 */

export default function FeedbackFilterGroupContainer({ isResponsePerRowLayout = false }) {
  const { comparisons } = useGetComparisons()
  const history = useHistory()
  const baseFilterOptions = useFilterOptions('feedback')

  const options = useMemo(() => {
    const filteredBaseOptions = isResponsePerRowLayout
      ? baseFilterOptions.filter((option) => option.field !== 'survey')
      : baseFilterOptions
    return filteredBaseOptions
  }, [baseFilterOptions, isResponsePerRowLayout])

  const {
    url,
    // @ts-ignore
    params: { viewToken },
  } = useRouteMatch()

  const isOpeningResponseDetailModal = window.location.href.includes('responses')

  const handleFilterChange = useCallback(
    /** @param {OnChangeValue<Extra>} filter */
    (filter) => {
      const prevState = decodeFilterFromUrlParam(history.location.search)
      const state = { ...prevState, advancedFilterQuery: filter }
      const search = encodeFilterToUrlParam(state)
      if (history.location.search !== search && !isOpeningResponseDetailModal) {
        history.replace({
          ...history.location,
          search,
        })
      }
    },
    [history, isOpeningResponseDetailModal]
  )

  const search = history.location.search
  const state = decodeFilterFromUrlParam(search)
  const advancedFilterRules = R.path(['advancedFilterQuery', 'rules'], state) || null
  const advancedFilterGroups = R.path(['advancedFilterQuery', 'groups'], state) || null
  const condition = R.path(['advancedFilterQuery', 'condition'], state) || 'and'
  const globalOperator = R.path(['advancedFilterQuery', 'globalOperator'], state) || 'and'

  const isFeedbackViewPage = url.includes('feedback/views')

  if (advancedFilterRules == null && advancedFilterGroups == null && isFeedbackViewPage) {
    return null
  }

  const renderKey = viewToken + JSON.stringify(advancedFilterRules ?? advancedFilterGroups)

  if (!isFeedbackViewPage || R.path(['advancedFilterQuery', 'globalOperator'], state)) {
    return (
      <FilterGroupV2
        key={renderKey}
        onChange={handleFilterChange}
        options={options}
        defaultGlobalOperator={globalOperator}
        defaultValue={advancedFilterGroups}
        comparisons={comparisons}
        allowKeepingSelectedOption={true}
      />
    )
  } else {
    return (
      <FilterGroup
        key={renderKey}
        condition={condition}
        onChange={handleFilterChange}
        options={options}
        defaultValue={advancedFilterRules}
        comparisons={comparisons}
      />
    )
  }
}

export function SentimentInput({ defaultFilters, onChange }) {
  const optionMap = {
    positive: { key: 'positive', text: 'Positive', value: 'positive', icon: 'green smile' },
    neutral: { key: 'neutral', text: 'Neutral', value: 'neutral', icon: 'yellow meh' },
    negative: { key: 'negative', text: 'Negative', value: 'negative', icon: 'red frown' },
  }
  const options = Object.keys(optionMap).map((key) => optionMap[key])

  const defaultValue = defaultFilters.map((filter) => filter.value)

  function handleChange(_, { value }) {
    onChange(
      value.map((sentiment) => {
        return {
          value: sentiment,
          label: optionMap[sentiment].text,
        }
      })
    )
  }
  return (
    <AutoFocusSemanticUiComponent functionName="open">
      <Dropdown
        placeholder="Sentiment"
        fluid
        multiple
        selection
        onChange={handleChange}
        options={options}
        defaultValue={defaultValue}
      />
    </AutoFocusSemanticUiComponent>
  )
}

export function TopicSentimentInput({ defaultFilters, onChange }) {
  const optionMap = {
    positive: { key: 'positive', text: 'Positive', value: 'positive', icon: 'green smile' },
    undefined: { key: 'neutral', text: 'Neutral', value: 'undefined', icon: 'yellow meh' },
    negative: { key: 'negative', text: 'Negative', value: 'negative', icon: 'red frown' },
  }
  const options = Object.keys(optionMap).map((key) => optionMap[key])

  const defaultValue = defaultFilters.map((filter) => filter.value)

  function handleChange(_, { value }) {
    onChange(
      value.map((sentiment) => {
        return {
          value: sentiment,
          label: optionMap[sentiment].text,
        }
      })
    )
  }
  return (
    <AutoFocusSemanticUiComponent functionName="open">
      <Dropdown
        placeholder="Sentiment"
        fluid
        multiple
        selection
        onChange={handleChange}
        options={options}
        defaultValue={defaultValue}
      />
    </AutoFocusSemanticUiComponent>
  )
}

export function EmailEventInput({ defaultFilters, onChange }) {
  const optionMap = {
    initial_email: { key: 'initial', text: 'Initial', value: 'initial_email' },
    follow_up_email_1: { key: 'follow_up_1', text: 'Follow-up 1', value: 'follow_up_email_1' },
    follow_up_email_2: { key: 'follow_up_2', text: 'Follow-up 2', value: 'follow_up_email_2' },
    follow_up_email_3: { key: 'follow_up_3', text: 'Follow-up 3', value: 'follow_up_email_3' },
  }
  const options = Object.keys(optionMap).map((key) => optionMap[key])

  const defaultValue = defaultFilters.map((filter) => filter.value)

  function handleChange(_, { value }) {
    onChange(
      value.map((email_event) => {
        return {
          value: email_event,
          label: optionMap[email_event].text,
        }
      })
    )
  }
  return (
    <AutoFocusSemanticUiComponent functionName="open">
      <Dropdown
        placeholder="Email event"
        fluid
        multiple
        selection
        onChange={handleChange}
        options={options}
        defaultValue={defaultValue}
      />
    </AutoFocusSemanticUiComponent>
  )
}

export function LanguageInput({ defaultFilters, onChange }) {
  const optionMap = Object.entries(languageOption).map(([label, value]) => ({
    key: value,
    text: label,
    value: label,
  }))

  const optionByValue = Object.fromEntries(optionMap.map((opt) => [opt.value, opt]))

  const defaultValue = defaultFilters.map((filter) => filter.value)

  function handleChange(_, { value }) {
    onChange(
      value.map((language) => {
        return {
          value: language,
          label: optionByValue[language].text,
        }
      })
    )
  }
  return (
    <AutoFocusSemanticUiComponent functionName="open">
      <Dropdown
        placeholder="Language"
        fluid
        multiple
        selection
        search
        onChange={handleChange}
        options={optionMap}
        defaultValue={defaultValue}
      />
    </AutoFocusSemanticUiComponent>
  )
}

export function SentimentDropdown({ value, onChange }) {
  const optionMap = {
    positive: { key: 'positive', text: 'Positive', value: 'positive', icon: 'green smile' },
    undefined: { key: 'neutral', text: 'Neutral', value: 'undefined', icon: 'yellow meh' },
    negative: { key: 'negative', text: 'Negative', value: 'negative', icon: 'red frown' },
  }
  const options = Object.keys(optionMap).map((key) => optionMap[key])

  function handleChange(_, { value }) {
    onChange(value)
  }
  return (
    <Dropdown
      placeholder="Sentiment"
      fluid
      multiple={false}
      selection
      onChange={handleChange}
      options={options}
      value={value}
    />
  )
}

export function ChoiceValueInput({ defaultFilters, onChange }) {
  const options = [
    { key: '0', text: '0', value: '0' },
    { key: '1', text: '1', value: '1' },
    { key: '2', text: '2', value: '2' },
    { key: '3', text: '3', value: '3' },
    { key: '4', text: '4', value: '4' },
    { key: '5', text: '5', value: '5' },
    { key: '6', text: '6', value: '6' },
    { key: '7', text: '7', value: '7' },
    { key: '8', text: '8', value: '8' },
    { key: '9', text: '9', value: '9' },
    { key: '10', text: '10', value: '10' },
  ]
  const defaultValue = defaultFilters.map((filter) => filter.value)

  function handleChange(_, { value }) {
    onChange(
      value.map((choiceValue) => {
        return {
          value: choiceValue,
        }
      })
    )
  }
  return (
    <AutoFocusSemanticUiComponent functionName="open">
      <Dropdown
        placeholder="Choice"
        fluid
        multiple
        selection
        onChange={handleChange}
        options={options}
        defaultValue={defaultValue}
      />
    </AutoFocusSemanticUiComponent>
  )
}

export function MetricInput({ defaultFilters, onChange }) {
  const options = [
    { key: 'CSAT', text: 'CSAT', value: 'CSAT' },
    { key: 'NPS', text: 'NPS', value: 'NPS' },
    { key: 'CES', text: 'CES', value: 'CES' },
    { key: '5-STAR', text: '5-STAR', value: '5-STAR' },
  ]
  const defaultValue = defaultFilters.map((filter) => filter.value)

  function handleChange(_, { value }) {
    onChange(
      value.map((metric) => {
        return {
          value: metric,
        }
      })
    )
  }
  return (
    <AutoFocusSemanticUiComponent functionName="open">
      <Dropdown
        placeholder="Metric"
        fluid
        multiple
        selection
        onChange={handleChange}
        options={options}
        defaultValue={defaultValue}
      />
    </AutoFocusSemanticUiComponent>
  )
}

export function PublishedAsTestimonialInput({ defaultFilters, onChange }) {
  const options = [{ key: 'true', text: 'true', value: true }]
  const defaultValue = defaultFilters.map((filter) => filter.value)

  function handleChange(_, { value }) {
    onChange(
      value.map((metric) => {
        return {
          value: metric,
        }
      })
    )
  }
  return (
    <AutoFocusSemanticUiComponent functionName="open">
      <Dropdown
        fluid
        selection
        onChange={handleChange}
        options={options}
        defaultValue={defaultValue}
        clearable={false}
      />
    </AutoFocusSemanticUiComponent>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/FeedbackFilterGroup/container.js